import { AUTH_TOKEN } from 'app/constants';
import { HttpError, HttpErrorProps } from 'app/components/utils/http-error';
import { ExtendedTasksResponse } from 'app/console-api/tasks/tasks-extended';
import { RewardsValue } from 'app/console-api/apps/types';
import { AutomationRequest } from 'app/console-api/automations/types';

export interface CreateTaskRequest {
  pluginID: number;
  name: string;
  description: string;
  pluginMetadata: object | null;
  customMetadata: object | null;
  rewards: RewardsValue[];
  categories: number[];
  automations: AutomationRequest[];
}

export interface UpdateTaskRequest {
  pluginID: number;
  isActive: boolean;
  name: string;
  description: string;
  pluginMetadata: object | null;
  customMetadata: object | null;
  rewards: RewardsValue[];
  categories: number[];
  automations: AutomationRequest[];
}

export enum TaskStatus {
  NotStarted = 'not_started',
  Inactive = 'inactive',
  Active = 'active',
  Completed = 'completed'
}

export interface Task {
  id: string;
  organizationID: string;
  appID: string;
  pluginID: number;
  name: string;
  description: string;
  status: TaskStatus;
  pluginMetadata: object | null;
  customMetadata: object;
  isActive: boolean;
  rewards: RewardsValue[];
  createdAt: Date;
  updatedAt: Date;
}

export const createTask = async (appID: string, data: CreateTaskRequest): Promise<Task[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/tasks`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to create task: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to create task: ${response.statusText}`, response.status)
      );
    }
  }
  return await response.json();
};

export const getTaskById = async (
  appID: string,
  taskID: string
): Promise<ExtendedTasksResponse> => {
  let token = localStorage.getItem(AUTH_TOKEN) || '';
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/tasks/${taskID}`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get task by id: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};

export const updateTask = async (
  appID: string,
  id: string,
  data: UpdateTaskRequest
): Promise<Task> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/tasks/${id}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to update task: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to update task: ${response.statusText}`, response.status)
      );
    }
  }

  return await response.json();
};
